import { Row, Col } from 'react-bootstrap';
import { BaseWidgetWithDeviceProps, Device } from 'types';
import WidgetBox, {
    CustomSpinner,
    WidgetContainer,
    WidgetContent,
    WidgetHeader,
    WidgetTitle,
    WidgetSubtitle,
} from '../WidgetBox';
import { useEffect, useState } from 'react';
import styled from '@emotion/styled';
import useCollectSort from 'hooks/useCollectSort';
import { fetchPSLStatus } from 'service/proqueryService';
import { get, isEmpty } from 'lodash';

type StatusItem = {
    deviceId: string;
    gatewayId: string;
    deviceName: string;
    deviceType: string;
    status: 0 | 1;
};

type PslStatusResponse = Awaited<ReturnType<typeof fetchPSLStatus>>;

function mapDevicesToStatusItems(devices: Device[], res: PslStatusResponse): StatusItem[] {
    return devices.map((device) => {
        const match = res.find(
            (row) => row.deviceId === device.deviceId && row.gatewayId === device.gatewayId
        );
        return {
            deviceId: device.deviceId,
            gatewayId: device.gatewayId,
            deviceName: device.deviceName,
            deviceType: device.deviceType,
            status: get(match, "status") as 0 | 1,
        } as StatusItem;
    });
}

/** Fills `.widget` and allows flex children to shrink instead of overflowing. */
const StatusWidgetShell = styled.div`
    height: 100%;
    min-height: 0;
    display: flex;
    flex-direction: column;
`;

const StatusWidgetContainer = styled(WidgetContainer)`
    flex: 1 1 auto;
    min-height: 0;
    overflow: hidden;
`;

const StatusWidgetContent = styled(WidgetContent)`
    flex: 1 1 auto;
    min-height: 0;
    display: flex;
    flex-direction: column;
    overflow: hidden;
`;

const TableStatus = styled.div`
    flex: 1 1 auto;
    min-height: 0;
    display: flex;
    flex-direction: column;
    overflow: hidden;

    .table__header {
        /* border: 1px solid #505C7480; */
        border-style: solid;
        border-color: #505C7480;
        border-width: 1px 0px 1px 0px;
        /* height: 36; */
        padding: 10px 28px 10px 10px;
        margin-bottom: 10px;
        flex-shrink: 0;
    }
    .table__body {
        flex: 1 1 auto;
        min-height: 0;
        max-height: 260px;
        overflow-y: auto;
        padding-right: 10px;

        &::-webkit-scrollbar {
            width: 5px;
            border-radius: 8px;
        }
        &::-webkit-scrollbar-track {
            border-radius: 5px;
            background-color: #3E4B67;
        }
        &::-webkit-scrollbar-thumb {
            border-radius: 5px;
            background-color: #828FAB;
        }
    }
`

const DotStatus = styled.span<{ status: number }>`
    display: inline-block;
    width: 12px;
    height: 12px;
    border-radius: 50%;
    margin-right: 8px;
    background-color: ${(props) => (props.status === 1 ? '#28a745' : '#dc3545')};
`;

const StatusWidget = ({
    panel: { name, attributes },
    isVisible,
    visibleRef,
    zoomLevel,
    deviceConfig,
}: BaseWidgetWithDeviceProps) => {
    const [isLoaded, setIsLoaded] = useState(false);
    const [data, setData] = useState<StatusItem[]>([]);
    const [devices, setDevices] = useState<Device[]>([]);

    useEffect(() => {
        setDevices(deviceConfig ?? []);
    }, [deviceConfig])

    useEffect(() => {
        if (isEmpty(devices)) {
            return;
        }
        const fectData = async () => {
            try {
                const res = await fetchPSLStatus(attributes);
                setData(mapDevicesToStatusItems(devices, res));
            } finally {
                setIsLoaded(false);
            }
        }

        fectData(); // call first time

        const refreshRate = 30 * 1000;
        const fetchInteval = setInterval(async () => {
            await fectData();
        }, refreshRate);

        return () => {
            fetchInteval && clearInterval(fetchInteval);
        }

    }, [name, devices, attributes]);

    const { data: dataList, sortIcon } = useCollectSort(data);

    useEffect(() => {
        if (isVisible && !isLoaded) {
            setIsLoaded(true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isVisible, isLoaded]);

    const getStatusText = (status: 0 | 1) => {
        if (Number.isNaN(Number(status))) {
            return '–';
        }
        return status === 1 ? 'On' : 'Off';
    }

    const renderContent = () => {
        return (
            <TableStatus className="psl-status-widget">
                {/* Header */}
                <div className="table__header">
                    <Row className="align-items-center">
                        <Col xs={12} sm={6} md={6} lg={7}>Name {sortIcon("deviceName")}</Col>
                        <Col xs={12} sm={3} md={3} lg={2}>Type {sortIcon("deviceType")}</Col>
                        <Col xs={12} sm={3} md={3} lg={3}>Status {sortIcon("status")}</Col>
                    </Row>
                </div>

                {/* Items */}
                <div className="table__body">
                    {dataList.map((it) => (
                        <Row key={it.deviceId} className="row-item align-items-center rounded bg-secondary px-2 py-2 mb-1">
                            <Col xs={12} sm={6} md={6} lg={7} className="text-break">
                                {it.deviceName}
                            </Col>
                            <Col xs={12} sm={3} md={3} lg={2} className="text-nowrap">
                                {it.deviceType}
                            </Col>
                            <Col xs={12} sm={3} md={3} lg={3} className="text-nowrap">
                                {it.status !== undefined && <DotStatus className="status-color" status={it.status} />}
                                <span style={{ verticalAlign: 'middle' }}>{getStatusText(it.status)}</span>
                            </Col>
                        </Row>
                    ))}
                </div>
            </TableStatus>
        )
    }

    return (
        <WidgetBox
            ref={visibleRef}
            className={
                zoomLevel <= 0.5
                    ? "widget-box w-100 h-30vh"
                    : "widget-box w-100"
            }
        >
            <div className="widget">
                <StatusWidgetShell>
                    <StatusWidgetContainer>
                        <WidgetHeader className="header mb-3 mb-md-4">
                            <div className='d-flex flex-column'>
                                <WidgetTitle>{name}</WidgetTitle>
                                <WidgetSubtitle>Live Status</WidgetSubtitle>
                            </div>
                        </WidgetHeader>

                        {isLoaded && (
                            <StatusWidgetContent className="mt-md-auto flex-fill">
                                {renderContent()}
                            </StatusWidgetContent>
                        )}
                        {!isLoaded && (
                            <div className="d-flex justify-content-center align-items-center mt-n5 flex-fill">
                                <CustomSpinner animation="border" />
                            </div>
                        )}
                    </StatusWidgetContainer>
                </StatusWidgetShell>
            </div>
        </WidgetBox>

    );
};

export default StatusWidget;